/**
 * cognify-analytics.js — product view / add-to-cart beacon.
 *
 * WHY THIS EXISTS
 * Shopify does not retain per-product view data on this store. Measured
 * 2026-08-14 against ShopifyQL: the only datasets available are `sessions`,
 * `sales`, `customers` and `inventory`; the `sessions` dataset carries a single
 * metric (`sessions`) and no product dimension. The one product-shaped figure
 * it offers is `landing_page_path`, which counts only the page a session
 * *started* on — and 713 of 1920 sessions start on the homepage, so a visitor
 * who lands there and browses five products contributes nothing to any of them.
 *
 * "Which product did who look at, and how many people" therefore has to be
 * collected first-party. This posts those events to the Cognify analytics API,
 * which already models exactly this shape.
 *
 * WHAT IT SENDS
 *   POST {apiBase}/api/analytics/events
 *   { shopId, eventType, visitorId, sessionId, productIds, screenWidth, … }
 *
 * The endpoint is [AllowAnonymous] with a CORS policy that echoes any origin,
 * so no token rides on the storefront and the custom domain works.
 *
 * ── TWO FIELDS THAT ARE NOT OPTIONAL ─────────────────────────────────────────
 * The API scores every event for bot-likeness and DROPS anything scoring above
 * zero — one signal is enough. Two of those signals are ours to get right, and
 * getting either wrong makes this file appear to work while nothing is ever
 * stored:
 *
 *   screenWidth        missing or 0  → +30 → dropped
 *   sessionCreatedAt   >24h old      → +25 → dropped   (>72h → +50)
 *
 * So `sessionCreatedAt` is the start of *this session*, held in sessionStorage,
 * and must never be the visitor's first-seen date — that would classify every
 * returning visitor as a bot one day later.
 *
 * DESIGN NOTES
 * - The visitor id is a pseudonym: random, first-party, no name, email or IP.
 *   The API derives IP/country server-side from the request and explicitly does
 *   not trust the client for it.
 * - Fire and forget. Nothing here renders anything, and a failed request must
 *   never be visible to a shopper — this is deliberately unlike the old
 *   remote-config call removed from active-offers.js, which drove the UI.
 * - Consent is honoured the same way attribution.js does it.
 */
(function () {
  'use strict';

  var cfg = window.cognifyAnalytics || {};
  if (!cfg.shopId || !cfg.apiBase) return;

  var VISITOR_KEY = 'up_vid';
  var SESSION_KEY = 'up_sid';
  var SESSION_AT_KEY = 'up_sid_at';
  var SENT_KEY = 'up_view_sent';

  // ── consent ───────────────────────────────────────────────────────────────
  // The privacy API loads asynchronously. Deciding at boot would let a page
  // whose banner is still unanswered send anyway, so when a banner is present
  // but the API has not arrived we wait for visitorConsentCollected instead.
  function consentOk() {
    try {
      var p = window.Shopify && window.Shopify.customerPrivacy;
      if (!p || typeof p.analyticsProcessingAllowed !== 'function') return true;
      return p.analyticsProcessingAllowed() !== false;
    } catch (e) {
      return true;
    }
  }

  function consentApiReady() {
    var p = window.Shopify && window.Shopify.customerPrivacy;
    return !!(p && typeof p.analyticsProcessingAllowed === 'function');
  }

  if (!consentOk()) return;

  if (!consentApiReady() && document.documentElement.hasAttribute('data-shopify-privacy-banner')) {
    document.addEventListener('visitorConsentCollected', function onConsent() {
      document.removeEventListener('visitorConsentCollected', onConsent);
      if (consentOk()) start();
    });
    return;
  }

  // ── identity ──────────────────────────────────────────────────────────────
  function rand(n) {
    var out = '';
    var alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
    try {
      var buf = new Uint8Array(n);
      window.crypto.getRandomValues(buf);
      for (var i = 0; i < n; i++) out += alphabet[buf[i] % alphabet.length];
      return out;
    } catch (e) {
      for (var j = 0; j < n; j++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
      return out;
    }
  }

  /** Persistent pseudonym. `vis_` prefix is the format the API documents. */
  function visitorId() {
    try {
      var v = window.localStorage.getItem(VISITOR_KEY);
      if (v) return v;
      v = 'vis_' + rand(16);
      window.localStorage.setItem(VISITOR_KEY, v);
      return v;
    } catch (e) {
      return 'vis_' + rand(16); // private mode — a throwaway id beats no event
    }
  }

  /**
   * Per-session id and its birth time. Both live in sessionStorage so they
   * reset with the tab session, which is what keeps sessionCreatedAt young
   * enough to clear the bot filter. See the header note.
   */
  function session() {
    try {
      var s = window.sessionStorage.getItem(SESSION_KEY);
      var at = window.sessionStorage.getItem(SESSION_AT_KEY);
      if (s && at) return { id: s, at: at };
      s = 'ses_' + rand(16);
      at = new Date().toISOString();
      window.sessionStorage.setItem(SESSION_KEY, s);
      window.sessionStorage.setItem(SESSION_AT_KEY, at);
      return { id: s, at: at };
    } catch (e) {
      return { id: 'ses_' + rand(16), at: new Date().toISOString() };
    }
  }

  // ── sending ───────────────────────────────────────────────────────────────
  function send(eventType, productIds, variantIds, metadata) {
    var ses = session();
    var body = {
      shopId: cfg.shopId,
      campaignId: cfg.campaignId || '',
      eventType: eventType,
      visitorId: visitorId(),
      sessionId: ses.id,
      sessionCreatedAt: ses.at,
      productIds: productIds || [],
      variantIds: variantIds || [],
      timestamp: new Date().toISOString(),

      // Required to clear the bot filter — see header.
      screenWidth: window.screen && window.screen.width,
      screenHeight: window.screen && window.screen.height,
      userAgent: navigator.userAgent,
      language: navigator.language,
      metadata: metadata || {}
    };

    if (cfg.customerId) body.customerId = String(cfg.customerId);

    var url = String(cfg.apiBase).replace(/\/+$/, '') + '/api/analytics/events';

    try {
      window
        .fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
          mode: 'cors',
          credentials: 'omit'
        })
        .catch(function () {
          /* analytics must never surface to a shopper */
        });
    } catch (e) {
      /* ignore */
    }
  }

  // Entry point. Kept as a function so the consent gate above can defer it
  // until the shopper answers the banner.
  /**
   * The funnel stage this pageview alone demonstrates.
   * Mirrors attribution.js's STAGES so the two systems agree on vocabulary;
   * 'product' is already covered by the dedicated view event below, so this
   * only reports the stages that otherwise leave no trace.
   */
  function stageForPage() {
    var t = cfg.pageType;
    if (t === 'collection' || t === 'search' || t === 'article' || t === 'blog') return 'browse';
    if (t === 'cart') return null;      // add_to_cart already reports this
    if (t === 'product') return null;   // the view event below reports this
    return 'land';                      // index, page, everything else
  }

  /**
   * One row per stage per session. Without the guard a shopper browsing six
   * collections would look like six separate funnel entries, which would make
   * the browse→product drop-off look far better than it is.
   */
  function sendStageOnce(stage) {
    if (!stage) return;
    var mark = SENT_KEY + ':stage:' + stage;
    try {
      if (window.sessionStorage.getItem(mark) === '1') return;
      window.sessionStorage.setItem(mark, '1');
    } catch (e) {
      /* sessionStorage unavailable — send anyway, a duplicate beats a gap */
    }
    send(stage, [], [], { page_type: cfg.pageType || '', path: location.pathname.slice(0, 120) });
  }

  function start() {
    // ── funnel stage ──────────────────────────────────────────────────────────
    // WHY: the storefront already stamps a stage trail onto the cart
    // (attribution.js), but that trail only becomes readable if the visitor
    // BUYS — the cart rides through to the order. Everyone who dropped out
    // left no row at all, so "where do we lose people" had no answer.
    // These events are the missing rows.
    sendStageOnce(stageForPage());

    // ── product view ──────────────────────────────────────────────────────────
    // Once per product per session: a shopper flicking between variants or
    // hitting back should not inflate the view count.
    if (cfg.pageType === 'product' && cfg.productId) {
      var mark = SENT_KEY + ':' + cfg.productId;
      var already = false;
      try {
        already = window.sessionStorage.getItem(mark) === '1';
      } catch (e) {
        /* ignore */
      }
      if (!already) {
        send('view', [String(cfg.productId)], cfg.variantId ? [String(cfg.variantId)] : [], {
          handle: cfg.handle || '',
          title: cfg.productTitle || '',
          price: cfg.productPrice || null
        });
        try {
          window.sessionStorage.setItem(mark, '1');
        } catch (e) {
          /* ignore */
        }
      }
    }

    // ── checkout ──────────────────────────────────────────────────────────────
    // The most expensive drop-off in the funnel is cart→checkout, and it was
    // invisible: checkout runs on Shopify's own domain, so no theme script
    // gets to see it. We report the moment the shopper *commits* instead.
    //
    // Detection mirrors attribution.js: any checkout submit button, any link
    // into /checkout, and the accelerated payment buttons. Matching on intent
    // rather than on one element keeps this working across the cart page, the
    // drawer and quick-buy alike.
    //
    // send() already posts with keepalive:true, so the request survives the
    // navigation the click triggers — no separate beacon path needed.
    document.addEventListener(
      'click',
      function (e) {
        try {
          var el = e.target && e.target.closest
            ? e.target.closest('[name="checkout"], [href*="/checkout"], .shopify-payment-button')
            : null;
          if (!el) return;
          var mark = SENT_KEY + ':stage:checkout';
          try {
            if (window.sessionStorage.getItem(mark) === '1') return;
            window.sessionStorage.setItem(mark, '1');
          } catch (err) {
            /* ignore */
          }
          send('checkout', [], [], { via: el.tagName.toLowerCase() });
        } catch (err) {
          /* never let analytics break a checkout click */
        }
      },
      true
    );

    // ── add to cart ───────────────────────────────────────────────────────────
    // Patching fetch rather than listening for the theme's own events keeps this
    // working across every add path the theme has (product form, quick add, bulk
    // add, drawer) without coupling to any of them — the same reasoning as
    // attribution.js.
    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var result = nativeFetch.apply(this, arguments);
      try {
        var url = typeof input === 'string' ? input : input && input.url;
        if (url && url.indexOf('/cart/add') !== -1) {
          result
            .then(function (res) {
              if (!res || !res.ok) return;
              res
                .clone()
                .json()
                .then(function (data) {
                  var items = data && data.items ? data.items : [data];
                  var pids = [];
                  var vids = [];
                  items.forEach(function (it) {
                    if (!it) return;
                    if (it.product_id) pids.push(String(it.product_id));
                    if (it.variant_id || it.id) vids.push(String(it.variant_id || it.id));
                  });
                  if (pids.length) send('add_to_cart', pids, vids, {});
                })
                .catch(function () {});
            })
            .catch(function () {});
        }
      } catch (e) {
        /* ignore */
      }
      return result;
    };
  }

  start();
})();
